import React, { createContext, useState, useContext, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Info, X, Lock } from 'lucide-react';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notification, setNotification] = useState(null);

    const showNotification = useCallback((type, message, onConfirm = null) => {
        setNotification({ type, message, onConfirm });
        if (type !== 'confirm') {
            const duration = (type === 'error' || type === 'auth_error') ? 6000 : 3000;
            setTimeout(() => setNotification(null), duration);
        }
    }, []);

    const closeNotification = useCallback(() => {
        setNotification(null);
    }, []);

    return (
        <NotificationContext.Provider value={{ showNotification }}>
            {children}
            {notification && (
                <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4 bg-black/20 backdrop-blur-[2px] animate-in fade-in duration-200">
                    <div className={`bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 border-t-8 transform scale-in-center overflow-hidden ${notification.type === 'success' ? 'border-emerald-500' :
                            (notification.type === 'error' || notification.type === 'auth_error') ? 'border-red-500' : 'border-orange-400'
                        }`}>
                        <div className="flex flex-col items-center text-center gap-4">
                            <div className={`p-4 rounded-full ${notification.type === 'success' ? 'bg-emerald-50 text-emerald-500' :
                                    (notification.type === 'error' || notification.type === 'auth_error') ? 'bg-red-50 text-red-500' : 'bg-orange-50 text-orange-500'
                                }`}>
                                {notification.type === 'success' ? <CheckCircle className="w-10 h-10" /> :
                                    notification.type === 'auth_error' ? <Lock className="w-10 h-10" /> :
                                    notification.type === 'error' ? <AlertTriangle className="w-10 h-10" /> : <Info className="w-10 h-10" />}
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-slate-800 mb-2 lowercase first-letter:uppercase">
                                    {notification.type === 'success' ? 'success!' :
                                        (notification.type === 'error' || notification.type === 'auth_error') ? 'ops!' : 'confirmation'}
                                </h4>
                                {Array.isArray(notification.message) ? (
                                    <ul className="text-left text-slate-500 leading-relaxed font-medium space-y-1 mt-1">
                                        {notification.message.map((msg, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <span className="text-red-400 mt-0.5">•</span>
                                                <span>{msg}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-slate-500 leading-relaxed font-medium">{notification.message}</p>
                                )}
                            </div>

                            {notification.type === 'confirm' ? (
                                <div className="flex gap-3 w-full mt-4">
                                    <button
                                        onClick={closeNotification}
                                        className="flex-1 py-3 px-4 border border-slate-200 rounded-2xl text-slate-600 font-bold hover:bg-slate-50 transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (notification.onConfirm) notification.onConfirm();
                                            closeNotification();
                                        }}
                                        className="flex-1 py-3 px-4 bg-orange-500 text-white rounded-2xl font-bold hover:bg-orange-600 shadow-lg shadow-orange-100 transition-all"
                                    >
                                        Continue
                                    </button>
                                </div>
                            ) : (
                                <div className="w-full mt-4">
                                    <button
                                        onClick={closeNotification}
                                        className={`w-full py-3 px-4 rounded-2xl font-bold text-white transition-all shadow-lg ${
                                            notification.type === 'success'
                                                ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-100'
                                                : 'bg-red-500 hover:bg-red-600 shadow-red-100'
                                        }`}
                                    >
                                        OK
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes scale-in-center {
                    0% { transform: scale(0.95); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .scale-in-center { animation: scale-in-center 0.2s cubic-bezier(0.250, 0.460, 0.450, 0.940) both; }
            `}} />
        </NotificationContext.Provider>
    );
};
